import { DynamicPriceRulesFieldType } from '@modules/bcm/products/product/dynamic-price-rules-builder/models/dynamic-price-rules-field-type';
import { DynamicPriceRulesQueryOperator } from '@modules/bcm/products/product/dynamic-price-rules-builder/models/dynamic-price-rules-query-operator';
import { Product } from '@shared/models/product';
import { TenantRelation } from '@shared/models/tenant-relation';
import { Season } from '@shared/models/seasons';
import { Pier } from '@shared/models/pier';
import { Berth } from '@shared/models/berth';
import { WinterStorage } from '@shared/models/winter-storage';
import { DynamicPriceRuleBuilderConfig } from '@modules/bcm/products/product/dynamic-price-rules-builder/models/dynamic-price-rules.interfaces';
import { BcmTenantPermission } from '@modules/bcm/bcm-tenant-permission';

export enum DynamicPriceRulesEntityId {
    Boat = 'boat',
    Berth = 'berth',
    TenantRelation = 'tenantRelation',
    Season = 'season',
    WinterStorage = 'winterStorage',
    Product = 'product',
    Pier = 'pier'
}

export const dynamicPriceRulesEntityNames: { [key in DynamicPriceRulesEntityId]: string } = {
    [DynamicPriceRulesEntityId.Boat]: 'Boot',
    [DynamicPriceRulesEntityId.Berth]: 'Liegeplatz',
    [DynamicPriceRulesEntityId.Season]: 'Saison',
    [DynamicPriceRulesEntityId.WinterStorage]: 'Lagerplatz',
    [DynamicPriceRulesEntityId.Product]: 'Produkt',
    [DynamicPriceRulesEntityId.TenantRelation]: 'Beziehung',
    [DynamicPriceRulesEntityId.Pier]: 'Steg'
};

export enum DynamicPriceRulesPropertyId {
    Width = 'width',
    Length = 'length',
    LengthOverall = 'lengthOverall',
    Area = 'area',
    Sqm = 'sqm',
    Quantity = 'quantity',
    TotalEngineHP = 'totalEngineHP',
    Name = 'name',
    Type = 'type'
}

export const dynamicPriceRulesPropertyNames: { [key in DynamicPriceRulesPropertyId]: string } = {
    [DynamicPriceRulesPropertyId.Width]: 'Breite',
    [DynamicPriceRulesPropertyId.Length]: 'Länge',
    [DynamicPriceRulesPropertyId.LengthOverall]: 'Länge über Alles (LüA)',
    [DynamicPriceRulesPropertyId.Area]: 'Fläche (m²)',
    [DynamicPriceRulesPropertyId.Sqm]: 'Fläche (m²)',
    [DynamicPriceRulesPropertyId.Quantity]: 'Anzahl / Menge',
    [DynamicPriceRulesPropertyId.TotalEngineHP]: 'Gesamtleistung (PS)',
    [DynamicPriceRulesPropertyId.Name]: 'Bezeichnung',
    [DynamicPriceRulesPropertyId.Type]: 'Bootsart'
};

export function getDefaultRulesConfig(
    product: Product,
    tenantRelations: TenantRelation[],
    seasons: Season[],
    piers: Pier[],
    berths: Berth[] = [],
    winterStorages: WinterStorage[] = [],
    boatTypes: string[] | null = null
): DynamicPriceRuleBuilderConfig {
    return {
        product: product,
        entities: [
            {
                name: dynamicPriceRulesEntityNames[DynamicPriceRulesEntityId.Boat],
                id: DynamicPriceRulesEntityId.Boat,
                fields: [
                    {
                        name: dynamicPriceRulesPropertyNames.width,
                        property: DynamicPriceRulesPropertyId.Width,
                        propertyRounding: true,
                        type: DynamicPriceRulesFieldType.Number,
                        defaultOperator: DynamicPriceRulesQueryOperator.Equal,
                        defaultValue: 0
                    },
                    {
                        name: dynamicPriceRulesPropertyNames.length,
                        property: DynamicPriceRulesPropertyId.Length,
                        propertyRounding: true,
                        type: DynamicPriceRulesFieldType.Number,
                        defaultOperator: DynamicPriceRulesQueryOperator.Equal,
                        defaultValue: 0
                    },
                    {
                        name: dynamicPriceRulesPropertyNames.lengthOverall,
                        property: DynamicPriceRulesPropertyId.LengthOverall,
                        propertyRounding: true,
                        type: DynamicPriceRulesFieldType.Number,
                        defaultOperator: DynamicPriceRulesQueryOperator.Equal,
                        defaultValue: 0
                    },
                    {
                        name: dynamicPriceRulesPropertyNames.area,
                        property: DynamicPriceRulesPropertyId.Area,
                        propertyRounding: true,
                        type: DynamicPriceRulesFieldType.Number,
                        defaultOperator: DynamicPriceRulesQueryOperator.Equal,
                        defaultValue: 0
                    },
                    {
                        name: dynamicPriceRulesPropertyNames.totalEngineHP,
                        property: DynamicPriceRulesPropertyId.TotalEngineHP,
                        propertyRounding: true,
                        type: DynamicPriceRulesFieldType.Number,
                        defaultOperator: DynamicPriceRulesQueryOperator.Equal,
                        defaultValue: 0
                    },
                    {
                        name: dynamicPriceRulesPropertyNames.type,
                        property: DynamicPriceRulesPropertyId.Type,
                        type: DynamicPriceRulesFieldType.MultiSelect,
                        options: (boatTypes || []).map((boatType) => ({ name: boatType, value: boatType })),
                        defaultOperator: DynamicPriceRulesQueryOperator.Equal,
                        defaultValue: [],
                        optionsLoaded: boatTypes != null
                    }
                ]
            },
            {
                name: dynamicPriceRulesEntityNames[DynamicPriceRulesEntityId.Berth],
                id: DynamicPriceRulesEntityId.Berth,
                fields: [
                    {
                        name: dynamicPriceRulesPropertyNames.width,
                        property: DynamicPriceRulesPropertyId.Width,
                        type: DynamicPriceRulesFieldType.Number,
                        defaultOperator: DynamicPriceRulesQueryOperator.Equal,
                        defaultValue: 0
                    },
                    {
                        name: dynamicPriceRulesPropertyNames.length,
                        property: DynamicPriceRulesPropertyId.Length,
                        type: DynamicPriceRulesFieldType.Number,
                        defaultOperator: DynamicPriceRulesQueryOperator.Equal,
                        defaultValue: 0
                    },
                    {
                        name: dynamicPriceRulesPropertyNames.area,
                        property: DynamicPriceRulesPropertyId.Area,
                        type: DynamicPriceRulesFieldType.Number,
                        defaultOperator: DynamicPriceRulesQueryOperator.Equal,
                        defaultValue: 0
                    },
                    {
                        name: dynamicPriceRulesPropertyNames.name,
                        property: DynamicPriceRulesPropertyId.Name,
                        type: DynamicPriceRulesFieldType.Select,
                        options: (berths || []).map(({ handle, id }) => ({ name: handle, value: id })),
                        defaultOperator: DynamicPriceRulesQueryOperator.Equal
                    }
                ]
            },
            {
                name: dynamicPriceRulesEntityNames[DynamicPriceRulesEntityId.WinterStorage],
                id: DynamicPriceRulesEntityId.WinterStorage,
                fields: [
                    {
                        name: dynamicPriceRulesPropertyNames.width,
                        property: DynamicPriceRulesPropertyId.Width,
                        type: DynamicPriceRulesFieldType.Number,
                        defaultOperator: DynamicPriceRulesQueryOperator.Equal,
                        defaultValue: 0
                    },
                    {
                        name: dynamicPriceRulesPropertyNames.length,
                        property: DynamicPriceRulesPropertyId.Length,
                        type: DynamicPriceRulesFieldType.Number,
                        defaultOperator: DynamicPriceRulesQueryOperator.Equal,
                        defaultValue: 0
                    },
                    {
                        name: dynamicPriceRulesPropertyNames.name,
                        property: DynamicPriceRulesPropertyId.Name,
                        type: DynamicPriceRulesFieldType.Select,
                        options: (winterStorages || []).map(({ handle, id }) => ({ name: handle, value: id })),
                        defaultOperator: DynamicPriceRulesQueryOperator.Equal
                    }
                ]
            },
            {
                name: dynamicPriceRulesEntityNames[DynamicPriceRulesEntityId.TenantRelation],
                id: DynamicPriceRulesEntityId.TenantRelation,
                fields: [
                    {
                        type: DynamicPriceRulesFieldType.Select,
                        options: (tenantRelations || []).map(({ name, id }) => ({ name, value: id })),
                        defaultOperator: DynamicPriceRulesQueryOperator.Equal
                    }
                ]
            },
            {
                name: dynamicPriceRulesEntityNames[DynamicPriceRulesEntityId.Product],
                id: DynamicPriceRulesEntityId.Product,
                fields: [
                    {
                        name: dynamicPriceRulesPropertyNames.quantity,
                        property: DynamicPriceRulesPropertyId.Quantity,
                        type: DynamicPriceRulesFieldType.Number,
                        defaultOperator: DynamicPriceRulesQueryOperator.Equal,
                        defaultValue: 1
                    }
                ]
            },
            {
                name: dynamicPriceRulesEntityNames[DynamicPriceRulesEntityId.Season],
                id: DynamicPriceRulesEntityId.Season,
                fields: [
                    {
                        type: DynamicPriceRulesFieldType.Select,
                        options: (seasons || []).map(({ name, id }) => ({ name, value: id })),
                        defaultOperator: DynamicPriceRulesQueryOperator.Equal
                    }
                ],
                tenantPermission: BcmTenantPermission.SEASONS
            },
            {
                name: dynamicPriceRulesEntityNames[DynamicPriceRulesEntityId.Pier],
                id: DynamicPriceRulesEntityId.Pier,
                fields: [
                    {
                        type: DynamicPriceRulesFieldType.Select,
                        options: (piers || []).map(({ handle, id }) => ({ name: handle, value: id })),
                        defaultOperator: DynamicPriceRulesQueryOperator.Equal
                    }
                ]
            }
        ]
    };
}
